'use client';

import Link from 'next/link';
import { useRef, useEffect } from 'react';
import {
  useReducedMotion,
  useInView,
  useAnimate,
} from 'framer-motion';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LedgerRow {
  name: string;
  value: number;
  /** Count-up duration in ms — magnitude-scaled per animatic */
  countDuration: number;
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

const ROWS: LedgerRow[] = [
  { name: 'Stacked subscriptions (ChatGPT + Claude + Copilot)', value: 600, countDuration: 450 },
  { name: 'Max-plan premium over mid-tier', value: 1920, countDuration: 750 },
  { name: 'Wrong-model usage (premium for mini-class tasks)', value: 4800, countDuration: 975 },
  { name: 'Idle / forgotten API budget', value: 2400, countDuration: 750 },
  { name: 'Failed re-runs & retries', value: 1800, countDuration: 600 },
  { name: 'Duplicate-tool overlap', value: 2880, countDuration: 825 },
];

const TOTAL = 14400;
const TOTAL_COUNT_DURATION = 1575; // ms

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt(n: number): string {
  return '$' + Math.round(n).toLocaleString('en-US');
}

// easeOutExpo: matches GSAP's expo.out for the total counter
function easeOutExpo(t: number): number {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

// easeOutPower2: matches GSAP's power2.out for row counters
function easeOutPower2(t: number): number {
  return 1 - Math.pow(1 - t, 2);
}

// ---------------------------------------------------------------------------
// useLedgerOdometer — single shared RAF loop for all 7 counters
// ---------------------------------------------------------------------------

interface OdometerTarget {
  el: HTMLElement;
  finalValue: number;
  /** Time offset from loop start (ms) when this counter should begin */
  startOffset: number;
  duration: number;
  easeFn: (t: number) => number;
}

function useLedgerOdometer(
  containerRef: React.RefObject<HTMLElement | null>,
  shouldAnimate: boolean,
): void {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Reduced-motion: set final values immediately
    if (!shouldAnimate) {
      const rowAmounts = container.querySelectorAll<HTMLElement>('[data-odometer]');
      rowAmounts.forEach((el) => {
        const val = parseInt(el.dataset.odometer ?? '0', 10);
        el.textContent = fmt(val);
      });
      return;
    }

    // Build target list from DOM (rendered rows + total)
    const rowEls = container.querySelectorAll<HTMLElement>('[data-odometer-row]');
    const totalEl = container.querySelector<HTMLElement>('[data-odometer-total]');

    const targets: OdometerTarget[] = [];

    rowEls.forEach((el, idx) => {
      const row = ROWS[idx];
      if (!row) return;
      // Row counter starts at: row_t + 0.375s = (0.9 + idx*0.42 + 0.375) * 1000
      const startOffset = (0.9 + idx * 0.42 + 0.375) * 1000;
      targets.push({
        el,
        finalValue: row.value,
        startOffset,
        duration: row.countDuration,
        easeFn: easeOutPower2,
      });
    });

    if (totalEl) {
      // Total counter starts at 3.45*1.5*1000 = 5175ms
      targets.push({
        el: totalEl,
        finalValue: TOTAL,
        startOffset: 5175,
        duration: TOTAL_COUNT_DURATION,
        easeFn: easeOutExpo,
      });
    }

    // Initialize all to $0
    targets.forEach(({ el }) => { el.textContent = '$0'; });

    let rafId: number;
    const startTime = performance.now();

    function tick(now: number): void {
      const elapsed = now - startTime;
      let allDone = true;

      for (const target of targets) {
        const localElapsed = elapsed - target.startOffset;
        if (localElapsed < 0) {
          allDone = false;
          continue;
        }
        const progress = Math.min(localElapsed / target.duration, 1);
        if (progress < 1) allDone = false;
        const eased = target.easeFn(progress);
        target.el.textContent = fmt(eased * target.finalValue);
      }

      if (!allDone) {
        rafId = requestAnimationFrame(tick);
      }
    }

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [containerRef, shouldAnimate]);
}

// ---------------------------------------------------------------------------
// Arrow SVG (no emoji, no entity — clean SVG)
// ---------------------------------------------------------------------------

function ArrowRight(): React.JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 9h12M10 4l5 5-5 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface HeroSectionProps {
  isAuthenticated?: boolean;
}

export default function HeroSection({ isAuthenticated = false }: HeroSectionProps = {}): React.JSX.Element {
  const prefersReduced = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const ledgerRef = useRef<HTMLDivElement>(null);
  const [scope, animate] = useAnimate();

  // IntersectionObserver gate — fires once when hero enters viewport
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 });

  // Odometer hook — RAF loop for all 7 counters
  useLedgerOdometer(ledgerRef, isInView && !prefersReduced);

  // If reduced-motion: set all odometer spans to final values immediately
  useEffect(() => {
    if (!prefersReduced) return;
    const ledger = ledgerRef.current;
    if (!ledger) return;
    const rowEls = ledger.querySelectorAll<HTMLElement>('[data-odometer-row]');
    rowEls.forEach((el, idx) => {
      const row = ROWS[idx];
      if (row) el.textContent = fmt(row.value);
    });
    const totalEl = ledger.querySelector<HTMLElement>('[data-odometer-total]');
    if (totalEl) totalEl.textContent = fmt(TOTAL);
  }, [prefersReduced]);

  // GSAP-matching framer-motion entrance + ambient sequence
  useEffect(() => {
    if (!isInView) return;
    if (prefersReduced) return;

    const container = scope.current as HTMLElement | null;
    if (!container) return;

    // We use animate() from useAnimate to drive all entrance motions
    void (async () => {
      // Header eyebrow: from y:24,opacity:0 → resting, starts 0.225s
      animate(
        '[data-anim="eyebrow"]',
        { y: [24, 0], opacity: [0, 1] },
        { duration: 0.825, ease: [0.16, 1, 0.3, 1], delay: 0.225 },
      );
      // Header period
      animate(
        '[data-anim="period"]',
        { y: [18, 0], opacity: [0, 1] },
        { duration: 0.75, ease: 'easeOut', delay: 0.375 },
      );
      // Double rule: scaleX 0→1
      animate(
        '[data-anim="hdr-rule"]',
        { scaleX: [0, 1] },
        { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.45 },
      );

      // Six ledger rows — staggered per animatic
      ROWS.forEach((_, idx) => {
        const t = 0.9 + idx * 0.42; // row start time (seconds)
        animate(
          `[data-anim="row-name-${idx}"]`,
          { y: [24, 0], opacity: [0, 1] },
          { duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: t },
        );
        animate(
          `[data-anim="row-leader-${idx}"]`,
          { scaleX: [0, 1] },
          { duration: 0.48, ease: [0.16, 1, 0.3, 1], delay: t + 0.15 },
        );
        animate(
          `[data-anim="row-amount-${idx}"]`,
          { y: [30, 0], opacity: [0, 1] },
          { duration: 0.675, ease: [0.16, 1, 0.3, 1], delay: t + 0.27 },
        );
      });

      // Heavy rule: 4.275s
      animate(
        '[data-anim="heavy-rule"]',
        { scaleX: [0, 1] },
        { duration: 0.75, ease: [0.16, 1, 0.3, 1], delay: 4.275 },
      );

      // Total label: 4.725s
      animate(
        '[data-anim="total-label"]',
        { y: [18, 0], opacity: [0, 1] },
        { duration: 0.675, ease: 'easeOut', delay: 4.725 },
      );

      // Total value stamp: 4.95s
      animate(
        '[data-anim="total-value"]',
        { y: [60, 0], opacity: [0, 1], scale: [1.04, 1] },
        { duration: 0.825, ease: [0.16, 1, 0.3, 1], delay: 4.95 },
      );

      // Marker sweep: scaleX 0→1 at 6.3s
      animate(
        '[data-anim="total-marker"]',
        { scaleX: [0, 1] },
        { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 6.3 },
      );

      // Descriptor: 7.275s
      animate(
        '[data-anim="descriptor"]',
        { y: [28, 0], opacity: [0, 1] },
        { duration: 0.825, ease: [0.16, 1, 0.3, 1], delay: 7.275 },
      );

      // Methodology: 8.1s
      animate(
        '[data-anim="methodology"]',
        { opacity: [0, 1] },
        { duration: 0.75, ease: 'easeOut', delay: 8.1 },
      );

      // CTA: 8.25s
      animate(
        '[data-anim="cta"]',
        { y: [16, 0], opacity: [0, 1] },
        { duration: 0.75, ease: [0.16, 1, 0.3, 1], delay: 8.25 },
      );

      // Arrow back-out: 8.55s
      animate(
        '[data-anim="cta-arrow"]',
        { x: [-10, 0], opacity: [0, 1] },
        { duration: 0.6, ease: [0.34, 1.56, 0.64, 1], delay: 8.55 },
      );

      // --- Ambient (one finite cycle each) ---

      // Marker pulse: 9.0s → opacity 0.32→0.5→0.32 over 1.5s
      animate(
        '[data-anim="total-marker"]',
        { opacity: [0.32, 0.5, 0.32] },
        { duration: 1.5, ease: 'easeInOut', delay: 9.0 },
      );

      // Arrow nudge: 9.225s → x 0→6→0 over ~1.35s
      animate(
        '[data-anim="cta-arrow"]',
        { x: [0, 6, 0] },
        { duration: 1.35, ease: 'easeInOut', delay: 9.225 },
      );
    })();
  }, [isInView, prefersReduced, animate, scope]);

  // Reduced-motion: static final state
  const staticState = !!prefersReduced;

  return (
    <section
      ref={sectionRef}
      aria-label="Hero"
      className="relative min-h-dvh bg-paper"
    >
      {/* FT-style top ink bar */}
      <div className="absolute inset-x-0 top-0 h-1.5 bg-ink" aria-hidden="true" />

      {/* Animation scope wrapper */}
      <div ref={scope} className="flex min-h-dvh items-center justify-center px-6 py-24 md:px-10 lg:px-16">
        {/* Ledger column */}
        <div
          ref={ledgerRef}
          className="w-full max-w-[1180px]"
        >
          {/* ── Header ── */}
          <div className="flex items-baseline justify-between pb-3.5">
            <span
              data-anim="eyebrow"
              className="font-sans text-[clamp(13px,1.15vw,18px)] font-medium uppercase tracking-[0.22em] text-graphite"
              style={staticState ? undefined : { opacity: 0 }}
            >
              AI Waste — Individual Contributor Ledger
            </span>
            <span
              data-anim="period"
              className="font-mono text-[clamp(13px,1.15vw,18px)] font-medium uppercase tracking-[0.18em] text-graphite"
              style={staticState ? undefined : { opacity: 0 }}
            >
              FY26
            </span>
          </div>

          {/* Double rule */}
          <div
            data-anim="hdr-rule"
            className="relative h-1.5"
            aria-hidden="true"
            style={{
              transformOrigin: '0% 50%',
              ...(staticState ? undefined : { transform: 'scaleX(0)' }),
            }}
          >
            <div className="absolute inset-x-0 top-0 h-px bg-ink" />
            <div className="absolute inset-x-0 bottom-0 h-px bg-ink" />
          </div>

          {/* ── Ledger rows ── */}
          <ul role="list" className="mt-7 flex list-none flex-col p-0">
            {ROWS.map((row, idx) => (
              <li
                key={row.name}
                className="flex items-baseline text-[clamp(16px,2vw,32px)] leading-[1.7] text-ink"
              >
                {/* Name */}
                <span
                  data-anim={`row-name-${idx}`}
                  className="font-sans font-normal tracking-[-0.005em] whitespace-nowrap"
                  style={staticState ? undefined : { opacity: 0 }}
                >
                  {row.name}
                </span>

                {/* Dot leader — outer positions vertically, inner animates scaleX only */}
                <span
                  className="mx-[clamp(8px,1vw,18px)] min-w-0 flex-1 translate-y-[-9px]"
                  aria-hidden="true"
                >
                  <span
                    data-anim={`row-leader-${idx}`}
                    className="block"
                    style={{
                      height: '1px',
                      borderBottom: '2px dotted rgba(26,25,22,0.35)',
                      transformOrigin: '0% 50%',
                      ...(staticState ? undefined : { transform: 'scaleX(0)' }),
                    }}
                  />
                </span>

                {/* Amount odometer */}
                <span
                  data-anim={`row-amount-${idx}`}
                  data-odometer-row
                  data-odometer={row.value}
                  className="font-mono font-medium tabular-nums tracking-[-0.01em] whitespace-nowrap text-[clamp(14px,1.85vw,30px)]"
                  style={staticState ? undefined : { opacity: 0 }}
                >
                  {fmt(row.value)}
                </span>
              </li>
            ))}
          </ul>

          {/* Heavy rule above total */}
          <div
            data-anim="heavy-rule"
            className="relative mt-6 h-2"
            aria-hidden="true"
            style={{
              transformOrigin: '0% 50%',
              ...(staticState ? undefined : { transform: 'scaleX(0)' }),
            }}
          >
            <div className="absolute inset-x-0 top-0 h-0.5 bg-ink" />
            <div className="absolute inset-x-0 bottom-0 h-px bg-ink" />
          </div>

          {/* ── Total row ── */}
          <div className="mt-9 flex items-end justify-between">
            {/* Label */}
            <div
              data-anim="total-label"
              className="pb-7 font-sans text-[clamp(13px,1.15vw,20px)] font-semibold uppercase tracking-[0.24em] text-graphite"
              style={staticState ? undefined : { opacity: 0 }}
            >
              Total Waste / yr
            </div>

            {/* Value + marker */}
            <div className="relative inline-block">
              {/* Coral marker (highlight) */}
              <span
                data-anim="total-marker"
                className="absolute -left-3 -right-3 bottom-[18px] z-0 h-[42px] bg-burn"
                aria-hidden="true"
                style={{
                  opacity: 0.32,
                  transformOrigin: '0% 50%',
                  ...(staticState ? undefined : { transform: 'scaleX(0)' }),
                }}
              />
              {/* Total amount */}
              <span
                data-anim="total-value"
                data-odometer-total
                className="relative z-10 font-serif-display text-[clamp(64px,8.75vw,168px)] leading-[0.92] tracking-[-0.025em] tabular-nums text-ink"
                aria-label={fmt(TOTAL)}
                style={staticState ? undefined : { opacity: 0 }}
              >
                {fmt(TOTAL)}
              </span>
            </div>
          </div>

          {/* ── Descriptor ── */}
          <p
            data-anim="descriptor"
            className="mt-14 font-serif-display text-[clamp(22px,2.4vw,38px)] italic leading-[1.25] tracking-[-0.005em] text-ink text-pretty text-center"
            style={staticState ? undefined : { opacity: 0 }}
          >
            What an AI-native engineer wastes in a year.
          </p>

          {/* ── CTA row ── */}
          <div className="mt-10 flex items-baseline justify-between border-t border-rule pt-7">
            {/* Methodology note */}
            <span
              data-anim="methodology"
              className="font-mono text-[clamp(12px,0.9vw,16px)] tracking-[0.04em] text-graphite"
              style={staticState ? undefined : { opacity: 0 }}
            >
              verify · getverbal.ai/14400
            </span>

            {/* CTA */}
            <Link
              href={isAuthenticated ? '/dashboard' : '/signup'}
              data-anim="cta"
              className="inline-flex items-center gap-2.5 font-sans text-[clamp(16px,1.3vw,22px)] font-medium tracking-[0.02em] text-ink hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-burn"
              style={staticState ? undefined : { opacity: 0 }}
            >
              {isAuthenticated ? 'Open dashboard' : 'Find yours'}
              <span
                data-anim="cta-arrow"
                className="text-burn"
                style={staticState ? undefined : { opacity: 0 }}
              >
                <ArrowRight />
              </span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
